/**
 * StylePicker — dizayn STILINI almashtiruvchi smooth segmentli slayder.
 *
 * Knob barmoqni KUZATADI (smooth), qo'yib yuborilganda eng yaqin stilga
 * snap bo'ladi. Segment nuqtasini yoki preview kartani bosib ham tanlanadi.
 * Rang EMAS — dizayn TILI o'zgaradi (blur, radius, soya, fon, neumorfizm).
 *
 * Stillar: Shisha · Klassik · Yumshoq · Neo · Tekis · Material.
 * compact=true  → slayder + joriy nom (profil menyusi).
 * compact=false → + preview kartalar grid (Sozlamalar).
 */

import { useCallback, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { STYLES, applyStyle, getStyle, type Style } from '@/shared/lib/style-theme';

// Har stil uchun kichik ko'rgazma (knob swatch + preview karta).
const PREVIEW: Record<Style, React.CSSProperties> = {
  glass: {
    background:
      'linear-gradient(135deg, hsl(217 80% 72% / 0.55), hsl(357 76% 62% / 0.4) 60%, hsl(268 60% 65% / 0.3))',
    border: '1px solid hsl(0 0% 100% / 0.7)',
    borderRadius: '7px',
    boxShadow: 'inset 0 1px 0 hsl(0 0% 100% / 0.85), 0 4px 10px hsl(222 32% 22% / 0.18)',
    backdropFilter: 'blur(2px)',
  },
  classic: {
    background: 'hsl(0 0% 100%)',
    border: '1px solid hsl(220 13% 78%)',
    borderRadius: '3px',
    boxShadow: '0 1px 2px hsl(220 13% 18% / 0.12)',
  },
  soft: {
    background: 'linear-gradient(135deg, hsl(225 30% 99%), hsl(225 24% 94%))',
    border: '1px solid hsl(220 13% 88%)',
    borderRadius: '9999px',
    boxShadow: '0 6px 16px hsl(224 28% 22% / 0.16)',
  },
  neo: {
    background: 'hsl(222 16% 90%)',
    borderRadius: '8px',
    boxShadow: '2px 2px 4px hsl(222 24% 78%), -2px -2px 4px hsl(0 0% 100%)',
  },
  flat: {
    background: 'hsl(220 16% 96%)',
    border: '1px solid hsl(220 13% 85%)',
    borderRadius: '6px',
  },
  material: {
    background: 'hsl(0 0% 100%)',
    border: '1px solid hsl(220 13% 92%)',
    borderRadius: '6px',
    boxShadow: '0 1px 2px hsl(220 13% 18% / 0.1), 0 4px 10px hsl(220 13% 18% / 0.14)',
  },
};

export function StylePicker({ compact = false }: { compact?: boolean }): React.ReactElement {
  const [style, setStyle] = useState<Style>(getStyle);
  const [dragPct, setDragPct] = useState<number | null>(null);
  const draggingRef = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const count = STYLES.length;
  const activeIdx = Math.max(
    0,
    STYLES.findIndex((s) => s.id === style),
  );
  const active = STYLES[activeIdx];

  const select = useCallback(
    (i: number): void => {
      const opt = STYLES[Math.min(count - 1, Math.max(0, i))];
      if (!opt) return;
      applyStyle(opt.id);
      setStyle(opt.id);
    },
    [count],
  );

  const ratioFromX = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return activeIdx / (count - 1);
    const r = el.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width));
  };

  const updateFromX = (clientX: number): void => {
    const ratio = ratioFromX(clientX);
    setDragPct(ratio * 100);
    select(Math.round(ratio * (count - 1)));
  };

  const snappedPct = count > 1 ? (activeIdx / (count - 1)) * 100 : 0;
  const knobPct = dragPct ?? snappedPct;

  return (
    <div className="select-none">
      {/* ── Smooth slayder track ── */}
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label="Dizayn stili"
        aria-valuemin={0}
        aria-valuemax={count - 1}
        aria-valuenow={activeIdx}
        aria-valuetext={active?.name}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          draggingRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          updateFromX(e.clientX);
        }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          updateFromX(e.clientX);
        }}
        onPointerUp={(e) => {
          draggingRef.current = false;
          setDragPct(null);
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={() => {
          draggingRef.current = false;
          setDragPct(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            select(activeIdx + 1);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            select(activeIdx - 1);
          }
        }}
        className={cn(
          'relative flex h-8 cursor-pointer touch-none items-center rounded-full border border-border bg-secondary px-1',
          'shadow-inner focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
        style={{
          backgroundImage: 'radial-gradient(circle, hsl(220 13% 55% / 0.2) 0.8px, transparent 1.3px)',
          backgroundSize: '8px 8px',
        }}
      >
        {/* Pozitsiya nuqtalari */}
        {STYLES.map((opt, i) => {
          const pct = count > 1 ? (i / (count - 1)) * 100 : 0;
          return (
            <span
              key={opt.id}
              aria-hidden
              className="pointer-events-none absolute top-1/2 size-1 rounded-full bg-foreground/20"
              style={{ left: `${String(pct)}%`, transform: `translate(-${String(pct)}%, -50%)` }}
            />
          );
        })}
        {/* Knob — joriy stil swatch'i */}
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-1/2 flex size-6 items-center justify-center rounded-full ring-1 ring-border',
            'shadow-[0_2px_6px_hsl(220_13%_18%/0.22)]',
            dragPct === null ? 'transition-[left] duration-300 ease-out' : 'transition-none',
          )}
          style={{
            left: `${String(knobPct)}%`,
            transform: `translate(-${String(knobPct)}%, -50%)`,
            background: 'var(--color-popover)',
          }}
        >
          <span className="size-3 rounded-full" style={PREVIEW[style]} />
        </span>
      </div>

      {/* ── Joriy stil nomi + tavsifi ── */}
      <div className="mt-1.5 flex items-baseline justify-center gap-1.5">
        <span className="text-xs font-semibold text-primary">{active?.name}</span>
        <span className="text-[10px] text-muted-foreground">· {active?.desc}</span>
      </div>

      {/* ── To'liq rejim: preview kartalar ── */}
      {!compact && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {STYLES.map((opt, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => {
                  select(i);
                }}
                aria-pressed={isActive}
                className={cn(
                  'flex flex-col gap-1.5 rounded-xl border p-2 text-left transition-colors',
                  isActive ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-muted',
                )}
              >
                <span className="h-9 w-full rounded-lg" style={PREVIEW[opt.id]} aria-hidden />
                <span className="text-[11px] font-semibold leading-tight text-foreground">
                  {opt.name}
                </span>
                <span className="text-[9px] leading-tight text-muted-foreground">{opt.desc}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
